import { useState, useEffect } from 'react';
import { api, PaymentConfig } from '../../lib/api';

export function CreditPurchaseCard() {
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getPaymentConfig()
      .then(setConfig)
      .catch(() => setConfig(null))
      .finally(() => setConfigLoading(false));
  }, []);

  const handlePurchase = async (amountCents: number, method: 'card' | 'crypto') => {
    setLoading(true);
    setError(null);

    try {
      if (method === 'card') {
        const result = await api.createCheckoutSession(amountCents);
        window.location.href = result.url;
      } else {
        const result = await api.createCryptoCheckout(amountCents);
        window.location.href = result.hostedUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleCustomPurchase = (method: 'card' | 'crypto') => {
    const dollars = parseFloat(customAmount);
    if (isNaN(dollars) || dollars < 1) {
      setError('Minimum purchase is $1.00');
      return;
    }
    if (dollars > 10000) {
      setError('Maximum purchase is $10,000.00');
      return;
    }
    handlePurchase(Math.round(dollars * 100), method);
  };

  if (configLoading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-32 mb-4"></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const paymentsConfigured = !!config;
  const purchaseAmounts = config?.purchaseAmounts ?? [
    { amountCents: 100, displayPrice: '$1', credits: 100 },
    { amountCents: 500, displayPrice: '$5', credits: 500 },
    { amountCents: 1000, displayPrice: '$10', credits: 1000 },
    { amountCents: 2500, displayPrice: '$25', credits: 2500 },
    { amountCents: 5000, displayPrice: '$50', credits: 5000 },
    { amountCents: 10000, displayPrice: '$100', credits: 10000 },
  ];

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-foreground">Purchase Credits</h2>
        <p className="text-sm text-muted-foreground mt-1">
          No subscription required. Buy credits starting at $1.00 and pay as you go.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {!paymentsConfigured && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
          Payment system is being set up. Purchases will be available soon.
        </div>
      )}

      {/* Predefined amounts */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
        {purchaseAmounts.map(({ amountCents, displayPrice, credits }) => (
          <button
            key={amountCents}
            onClick={() => handlePurchase(amountCents, 'card')}
            disabled={loading || !paymentsConfigured}
            className="flex flex-col items-center justify-center p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
          >
            <span className="text-lg font-bold text-foreground">{displayPrice}</span>
            <span className="text-xs text-muted-foreground">{credits} credits</span>
          </button>
        ))}
      </div>

      {/* Custom amount */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            $
          </span>
          <input
            type="number"
            min="1"
            max="10000"
            step="0.01"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder="Custom amount"
            disabled={!paymentsConfigured}
            className="w-full pl-7 pr-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
          />
        </div>
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          = {customAmount ? Math.round(parseFloat(customAmount || '0') * 100) : 0} credits
        </span>
      </div>

      {/* Payment buttons */}
      <div className="flex gap-3">
        <button
          onClick={() =>
            customAmount
              ? handleCustomPurchase('card')
              : handlePurchase(purchaseAmounts[1]?.amountCents ?? 500, 'card')
          }
          disabled={loading || !paymentsConfigured}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Processing...' : 'Pay with Card'}
        </button>
        <button
          onClick={() =>
            customAmount
              ? handleCustomPurchase('crypto')
              : handlePurchase(purchaseAmounts[1]?.amountCents ?? 500, 'crypto')
          }
          disabled={loading || !paymentsConfigured}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Pay with Crypto'}
        </button>
      </div>
    </div>
  );
}
